import { useState } from 'react';
import { getCurrentPosition } from '../utils/locationUtils';
import { getNearbyBuildings } from '../data/buildingLocations';

/**
 * Custom hook to find nearby available rooms
 */
export const useNearbyRooms = (examSchedule) => {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [nearbyRooms, setNearbyRooms] = useState([]);
  const [userLocation, setUserLocation] = useState(null);

  const findNearbyRooms = async (searchRadius = 500) => {
    setIsSearching(true);
    setError(null);
    
    try {
      // 1. Get user's current location
      const position = await getCurrentPosition();
      setUserLocation(position);
      
      // 2. Find nearby buildings
      const nearbyBuildings = getNearbyBuildings(
        position.latitude, 
        position.longitude,
        searchRadius
      );
      
      if (nearbyBuildings.length === 0) {
        setError(`No buildings found within ${searchRadius} meters`);
        setIsSearching(false);
        return;
      }
      
      // 3. Get current date and time
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      
      // 4. For each building, find available rooms
      const availableRooms = [];
      
      nearbyBuildings.forEach(building => {
        // Get all rooms in this building
        const roomsInBuilding = [...new Set(
          examSchedule
            .filter(exam => exam.building === building.building)
            .map(exam => exam.room)
        )];
        
        // Check availability for each room
        roomsInBuilding.forEach(room => {
          // Get all exams for this room
          const roomExams = examSchedule.filter(
            exam => exam.building === building.building && exam.room === room
          );
          
          // Get today's exams
          const todayExams = roomExams.filter(exam => {
            const examDate = new Date(exam.date);
            return examDate.toDateString() === now.toDateString();
          });
          
          // Categorize today's exams
          const ongoingExams = [];
          const upcomingExams = [];
          const completedExams = [];
          
          todayExams.forEach(exam => {
            // Parse exam time
            const [startTimeStr, endTimeStr] = exam.time.split(' - ');
            
            // Check if exam is ongoing, upcoming, or completed
            const [startTime, startPeriod] = startTimeStr.split(' ');
            const [startHour, startMinute] = startTime.split(':').map(Number);
            
            let examStartHour = startHour;
            if (startPeriod === 'PM' && examStartHour < 12) examStartHour += 12;
            if (startPeriod === 'AM' && examStartHour === 12) examStartHour = 0;
            
            const [endTime, endPeriod] = endTimeStr.split(' ');
            const [endHour, endMinute] = endTime.split(':').map(Number);
            
            let examEndHour = endHour;
            if (endPeriod === 'PM' && examEndHour < 12) examEndHour += 12;
            if (endPeriod === 'AM' && examEndHour === 12) examEndHour = 0;
            
            const currentTimeInMinutes = currentHour * 60 + currentMinutes;
            const startTimeInMinutes = examStartHour * 60 + startMinute;
            const endTimeInMinutes = examEndHour * 60 + endMinute;
            
            if (currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes) {
              ongoingExams.push(exam);
            } else if (currentTimeInMinutes < startTimeInMinutes) {
              upcomingExams.push(exam);
            } else {
              completedExams.push(exam);
            }
          });
          
          // Determine room availability status
          let availabilityStatus;
          if (ongoingExams.length > 0) {
            availabilityStatus = 'in-use';
          } else if (upcomingExams.length > 0) {
            availabilityStatus = 'available-now';
          } else {
            availabilityStatus = 'available-full-day';
          }
          
          availableRooms.push({
            building: building.building,
            buildingName: building.name,
            room,
            distance: building.distance,
            availabilityStatus,
            ongoingExams,
            upcomingExams,
            completedExams,
            allExamsToday: todayExams
          });
        });
      });
      
      // 5. Sort rooms by availability (full-day first, then available-now)
      const sortedRooms = availableRooms.sort((a, b) => {
        if (a.availabilityStatus === 'available-full-day' && b.availabilityStatus !== 'available-full-day') return -1;
        if (a.availabilityStatus !== 'available-full-day' && b.availabilityStatus === 'available-full-day') return 1;
        if (a.availabilityStatus === 'available-now' && b.availabilityStatus !== 'available-now') return -1;
        if (a.availabilityStatus !== 'available-now' && b.availabilityStatus === 'available-now') return 1;
        return a.distance - b.distance; // If same availability status, sort by distance
      });
      
      // Filter out rooms that are in use
      const availableRoomsOnly = sortedRooms.filter(room => 
        room.availabilityStatus !== 'in-use'
      );
      
      setNearbyRooms(availableRoomsOnly);
    } catch (err) {
      setError(`Failed to find nearby rooms: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  return {
    isSearching,
    error,
    nearbyRooms,
    userLocation,
    findNearbyRooms,
    resetNearbyRooms: () => {
      setNearbyRooms([]);
      setError(null);
    }
  };
};